import { createClient } from '@supabase/supabase-js'

// === Supabase ===
export const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL
export const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY
export const sb = createClient(SUPABASE_URL, SUPABASE_ANON_KEY)

// === API Proxy (Railway backend) ===
export const API_URL = import.meta.env.VITE_API_URL || 'https://insightful-tenderness-production-29de.up.railway.app'

// === Claude API ===
// CLAUDE_MODEL = modèle principal de génération de scripts (Merlin classique).
// Test Fable 5 en cours (2026-06) : si la qualité de génération est meilleure
// on garde, sinon revert via VITE_CLAUDE_MODEL ou ce default.
export const CLAUDE_MODEL = import.meta.env.VITE_CLAUDE_MODEL || 'claude-fable-5'
// Modèle "cheap" pour les tâches secondaires (parse query, rerank, polish) :
// reste sur Sonnet, pas concerné par le test Fable.
export const CLAUDE_MODEL_SONNET = 'claude-sonnet-4-5-20250929'

// === Credit costs per action ===
// Calibré sur pricing API avril 2026 + marge 80% strict (Anthropic SaaS standard).
// Méthode : pipelineCost / 0.20 / 0.01 (1 cr facturé = €0.01 perçu user, et le
// coût API doit représenter 20% de la valeur facturée → 80% margin).
// Sur Basic 3000 cr/30€ : 1 cr = €0.01 user value.
//
// Free plan = 600 cr LIFETIME one-shot après scan profil offert.
// Basic     = 3 000 cr/mois renouvelables
// Créateur  = 10 000 cr/mois
// Pro       = 50 000 cr/mois
export const CREDIT_COSTS = {
  script_opus:         120, // valeur €1.20, coût ~$0.24 (€0.22) → marge 80%
  script_sonnet:       120,
  regen_opus:          120,
  regen_sonnet:        120,
  chat_message:        38,  // valeur €0.38, coût ~$0.075 (€0.07) → marge 80%
  format_search:       30,  // valeur €0.30, coût ~$0.06 (€0.05) → marge 81%
  bio_analysis:        50,  // valeur €0.50, coût ~$0.10 (€0.09) → marge 80%
  video_analysis:      200, // valeur €2.00, coût ~$0.32 (€0.29) → marge 85%
  evaluation:          95,  // valeur €0.95, coût ~$0.19 (€0.17) → marge 80%
}

// Plan credit allowances
// Free : 600 cr LIFETIME one-shot — discovery budget après le scan profil
// (lui-même offert hors crédits). Une fois épuisé → conversion forcée.
// Bumpé de 300 → 600 le 2026-05-04 : à 300 cr, le user free brûlait son
// quota en format_search avant d'avoir généré 1 script (cf. analyse 4 mai :
// 19:1 ratio search/script). 600 cr laisse marge pour 2 scripts + 5 search +
// chats, donc le user atteint le wow moment "j'ai un script" avant le paywall.
// Paid : crédits MENSUELS renouvelables.
export const PLAN_CREDITS = {
  free:       600,
  basic:     3000,
  createur: 10000,
  pro:      50000,
}

// Plan metadata for pricing UI.
// `price` = beta-era discounted MONTHLY price the user pays right now.
// `regularPrice` = the future public monthly price once the beta closes.
// `priceAnnualMonthly` = monthly equivalent if billed annually (~30% off
//   vs monthly billing). Total annual = priceAnnualMonthly × 12.
// Both prices are FIGÉS À VIE pendant la béta (ancrage + loss aversion).
export const PLANS = [
  { id: 'free',     label: 'Free',     price: 0,    regularPrice: 0,    priceAnnualMonthly: 0,    credits: 600,   features: ['600 crédits offerts', 'Scan profil offert', '1 profil Instagram', 'Chat Merlin'] },
  { id: 'basic',    label: 'Basic',    price: 39,   regularPrice: 69,   priceAnnualMonthly: 29,   credits: 3000,  features: ['3 000 crédits/mois', 'Analyse profil au signup (figée)', 'Génération de scripts', 'Polish & évaluation', 'Chat Merlin'] },
  { id: 'createur', label: 'Créateur', price: 79,   regularPrice: 149,  priceAnnualMonthly: 59,   credits: 10000, features: ['10 000 crédits/mois', '✨ Mise à jour profil + stratégie', 'Signaux d\'audience étendus', 'Analyseur de bios approfondi', 'Export de scripts'], popular: true },
  { id: 'pro',      label: 'Pro',      price: 249,  regularPrice: 499,  priceAnnualMonthly: 179,  credits: 50000, features: ['50 000 crédits/mois', 'Profils illimités', 'Gestion multi-clients', 'Explorateur de formats', 'Support prioritaire'] },
]

// Beta flag — when true, UIs display regularPrice with a strike + a "PROMO BETA"
// badge next to the current price. Flip to false when the beta closes to hide
// the crossed-out price and the badge.
export const BETA_PRICING_ACTIVE = true

// === OpenAI API (embeddings — via proxy, no key needed in frontend) ===
// === Airtable API (via proxy, no PAT needed in frontend) ===

// === Funnel  -  ratio based on creator maturity ===
// launch = just starting, needs visibility first
// grow = has some audience, needs trust + authority
// monetize = has audience + trust, ready to sell
// scale = established, needs all three at scale
export const FUNNEL_RATIOS = {
  launch:   { tof: .60, mof: .30, bof: .10 },
  grow:     { tof: .35, mof: .45, bof: .20 },
  monetize: { tof: .25, mof: .30, bof: .45 },
  scale:    { tof: .35, mof: .35, bof: .30 },
  // Legacy keys for backward compatibility
  views:     { tof: .60, mof: .30, bof: .10 },
  authority: { tof: .35, mof: .45, bof: .20 },
  sales:     { tof: .25, mof: .30, bof: .45 },
  balanced:  { tof: .35, mof: .35, bof: .30 },
}

export const FUNNEL_LABELS = {
  tof: { name: 'Se faire connaitre', letter: 'D', color: 'orange', emoji: '👋', explain: 'Ces contenus attirent des nouvelles personnes qui ne te connaissent pas encore. Sans portee, pas de business.' },
  mof: { name: 'Creer du lien', letter: 'E', color: 'blue', emoji: '🤝', explain: "Ces contenus construisent la confiance et l'autorite. Les gens passent de 'je te decouvre' a 'je te fais confiance'." },
  bof: { name: 'Convertir', letter: 'C', color: 'ink', emoji: '🎯', explain: "Ces contenus poussent a l'action : acheter, s'inscrire, prendre RDV. Le moment de monetiser la confiance." },
}

export const MATURITY_STAGES = [
  { id: 'launch', label: 'Je demarre', desc: "Pas ou peu d'audience, besoin de visibilite d'abord", emoji: '🚀' },
  { id: 'grow', label: "J'ai une audience", desc: 'Besoin de construire la confiance et la credibilite', emoji: '📈' },
  { id: 'monetize', label: 'Je monetise', desc: "J'ai l'audience et la confiance, je veux vendre", emoji: '💰' },
  { id: 'scale', label: 'Je scale', desc: 'Business qui tourne, besoin de tout en parallele', emoji: '⚡' },
]

// Keep OBJECTIVES as alias for backward compat
export const OBJECTIVES = MATURITY_STAGES

export const DAYS = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche']

// === Tooltips  -  langage CM2 ===
export const TIPS = {
  seau: "Un bouton emotionnel qui fait reagir les gens.",
  tof: "Pour se faire connaitre  -  attirer des nouveaux.",
  mof: "Pour creer du lien  -  les gens apprennent a te connaitre.",
  bof: "Pour vendre  -  les gens passent a l'action.",
  arc_narratif: "Comment l'histoire de ta video est construite.",
  hook: "La toute premiere phrase. C'est elle qui decide si les gens restent ou partent.",
  tribu: "Le groupe de personnes qui se reconnait dans ce que tu dis.",
  douleur: "Le probleme concret que tes abonnes vivent au quotidien.",
  ennemi: "Ce qui empeche tes abonnes d'avancer (une croyance, un systeme, etc.).",
  desir: "Ce que tes abonnes veulent vraiment obtenir.",
  vehicule: "Ta methode, ta facon unique de les aider.",
  funnel: "Les 3 types de contenus : se faire connaitre, creer du lien, vendre.",
  canvas: "Un guide etape par etape pour ecrire ton script.",
}

// === Seau plain labels ===
export const SEAU_PLAIN = {
  tribu_identitaire: 'Qui ils sont',
  tribu_aspirationnelle: 'Qui ils veulent devenir',
  ennemi_visible: 'Ce qui les bloque',
  ennemi_systemique: 'Le systeme contre eux',
  ennemi_interieur: 'Leur doute interieur',
  douleur_quotidienne: 'Leur probleme',
  peur_profonde: 'Leur peur',
  honte_secrete: "Ce qu'ils n'osent pas dire",
  desir_tangible: "Ce qu'ils veulent",
  desir_identitaire: 'Qui ils veulent etre',
  emotion_dominante: "L'emotion touchee",
  vehicule_solution: 'Ta methode',
  levier_autorite: 'Ta credibilite',
  preuve_sociale: 'Preuve que ca marche',
  urgence_rarete: 'Maintenant ou jamais',
  curiosite_gap: 'Ca donne envie de savoir',
  nostalgie: 'Souvenir',
  mecanisme_viral: 'Ce qui fait partager',
  resultat_contre_intuitif: 'Effet surprise',
}
